const API_BASE = '/wapi/v1';

class ApiError extends Error {
  constructor({ method, url, status, cause, data }) {
    super(
      `Failed to query API ${method} ${url}${status ? ` (${status})` : ''}`
    );
    this.name = 'ApiError';
    this.cause = cause;
    this.message = data?.error || data?.message;
  }
}

function getHeaders(context, token, anonymous) {
  // const store = context.$store || context.store;
  if (anonymous) {
    return {};
  }
  const cookieToken = token || context.$cookies?.get('auth')?.token;
  if (cookieToken) {
    return { Authorization: `Bearer ` + cookieToken };
  } else {
    return {};
  }
}

async function performRequestCore(
  context,
  { path, method = 'get', params = {}, token = '', anonymous = false }
) {
  console.log('request core', path, token);
  const url = context.$config.WEB_API_URL + API_BASE + path;
  const response = await context.$axios({
    method,
    headers: getHeaders(context, token, anonymous),
    url,
    data: params,
    params: method === 'get' ? params : {},
  });
  return response;
}

async function performRequest(context, params) {
  let status = 0;
  let exception;
  try {
    const response = await performRequestCore(context, params);
    return response.data;
  } catch (e) {
    exception = e;
    status = e.response?.status;
  }
  if (status) {
    console.log('Failed api call', params.path);
    throw new ApiError({
      method: params.method,
      url: params.path,
      status: exception.response?.status,
      cause: exception,
      data: exception.response?.data,
    });
  } else {
    // retry if network failure
    try {
      const response = await performRequestCore(context, params);
      return response.data;
    } catch (e) {
      exception = e;
      status = e.response?.status;
      throw new ApiError({
        method: params.method,
        url: params.path,
        status: exception.response?.status,
        cause: exception,
        data: exception.response?.data,
      });
    }
  }
}

export default {
  saveCard: async (context) => {
    return await performRequest(context, {
      method: 'post',
      path: '/payment-card/generate-secret',
    });
  },

  confirmCard: async (context, params) => {
    return await performRequest(context, {
      method: 'post',
      path: '/payment-card',
      params,
    });
  },

  confirmOrderPayment: async (context, orderId) => {
    return await performRequest(context, {
      method: 'post',
      path: `/order/${orderId}/payment`,
    });
  },

  getPaymentMethods: async (context) => {
    const resp = await performRequest(context, {
      path: '/payment-cards',
    });
    return resp.paymentCards.sort((c1, c2) => {
      return c1.fingerprint > c2.fingerprint;
    });
  },

  getOrderSections: async (context, params) => {
    const sections = await performRequest(context, {
      path: `/order/sections`,
      params,
    });
    return sections;
  },

  getTranslations: async (context, params) => {
    const translations = await performRequest(context, {
      path: `/translations`,
      params,
    });
    return translations;
  },

  getServicesWithSkills: async (context) => {
    const response = await performRequest(context, {
      params: { languages: ['lt', 'pl', 'en'] },
      path: `/services`,
      anonymous: true,
    });
    return response;
  },

  setDefaultPaymentMethod: async (context, params) => {
    await performRequest(context, {
      params,
      path: '/user/default-payment-method',
      method: 'put',
    });
    return 'OK';
  },

  setLanguage: async (context, params) => {
    await performRequest(context, {
      params,
      path: '/user/language',
      method: 'put',
    });
    return 'OK';
  },

  uploadImage: async (context, image) => {
    const formData = new FormData();
    formData.append('image', image);
    const res = await performRequest(context, {
      params: formData,
      path: `/image`,
      method: 'post',
    });
    return res;
  },

  login: async (context, params) => {
    const res = await performRequest(context, {
      params,
      path: '/login',
      method: 'post',
    });
    return res;
  },

  loginWithFrontendKey: async (context, params) => {
    const res = await performRequest(context, {
      params,
      path: '/loginWithKey',
      method: 'post',
    });
    return res;
  },

  register: async (context, params) => {
    const res = await performRequest(context, {
      params,
      path: '/register',
      method: 'post',
    });
    return res;
  },

  logBi: async (context, params) => {
    const res = await performRequest(context, {
      params,
      path: '/bi/events',
      method: 'post',
    });
    return res;
  },

  verifyLogin: async (context, params) => {
    const res = await performRequest(context, {
      params,
      path: '/preauth/' + params.id + '/verify',
      method: 'post',
    });
    return res;
  },

  getCustomer: async (context) => {
    const res = await performRequest(context, {
      path: '/customer',
    });
    return res;
  },

  getCompany: async (context, token) => {
    const res = await performRequest(context, {
      path: '/company',
      token,
    });
    return res;
  },

  getOrder: async (context, orderId) => {
    return await performRequest(context, {
      path: `/business/orders/${orderId}`,
    });
  },

  getChatroomMessages: async (context, chatroomId) => {
    return await performRequest(context, {
      path: `/chatrooms/${chatroomId}/messages`,
    });
  },

  openChatroom: async (context, chatroomId) => {
    return await performRequest(context, {
      path: `/chatrooms/${chatroomId}/open`,
      method: 'POST',
    });
  },

  sendChatMessage: async (context, chatroomId, message) => {
    return await performRequest(context, {
      path: `/chatrooms/${chatroomId}/message`,
      method: 'post',
      params: message,
    });
  },

  getInvoices: async (context) => {
    return await performRequest(context, {
      path: `/business/invoices`,
    });
  },

  getCompanyOrders: async (context, { type }) => {
    return await performRequest(context, {
      path: `/business/orders?type=` + type,
    });
  },

  transferOrder: async (context, orderId, params) => {
    return await performRequest(context, {
      path: `/order/${orderId}`,
      method: 'put',
      params,
    });
  },

  generateApiKey: async (context, params) => {
    return await performRequest(context, {
      path: `/company/key`,
      method: 'post',
      params,
    });
  },

  refreshToken: async (context) => {
    return await performRequest(context, {
      path: `/token`,
      method: 'post',
    });
  },

  getMessages: async (context, taskId) => {
    return await performRequest(context, { path: `/tasks/${taskId}/messages` });
  },

  getPro: async (context, proId) => {
    return await performRequest(context, { path: `/pros/${proId}` });
  },

  findPros: async (context, skillId, params) => {
    return await performRequest(context, {
      path: `/skills/${skillId}/pros`,
      params,
    });
  },

  registerCustomer: async (context, params) => {
    return await performRequest(context, {
      path: '/user',
      params,
      method: 'post',
    });
  },

  updateCustomer: async (context, params) => {
    return await performRequest(context, {
      path: '/user',
      params,
      method: 'put',
    });
  },

  updateCompany: async (context, params) => {
    return await performRequest(context, {
      path: '/company',
      params,
      method: 'put',
    });
  },

  postMessage: async (context, taskId, data) => {
    return await performRequest(context, {
      method: 'post',
      path: `/tasks/${taskId}/message`,
      params: data,
    });
  },

  confirmOrder: async (context, orderId) => {
    return await performRequest(context, {
      method: 'post',
      path: `/order/${orderId}/task`,
    });
  },

  confirmBusinessOrder: async (context, orderId, params = {}) => {
    return await performRequest(context, {
      method: 'post',
      path: `/business/order/${orderId}/task`,
      params,
    });
  },

  createOrder: async (context, params) => {
    return await performRequest(context, {
      method: 'post',
      path: `/business/order`,
      params,
    });
  },

  sendInvite: async (context, params) => {
    return await performRequest(context, {
      method: 'post',
      path: `/business/members/invite`,
      params,
    });
  },

  getBusinessMembers: async (context, params) => {
    return await performRequest(context, {
      method: 'get',
      path: `/business/members`,
      params,
    });
  },

  deleteMember: async (context, userId) => {
    return await performRequest(context, {
      method: 'delete',
      path: `/business/members/` + userId,
    });
  },

  setMemberRole: async (context, userId, params) => {
    return await performRequest(context, {
      method: 'post',
      path: `/business/members/` + userId,
      params,
    });
  },

  getInvite: async (context, invite) => {
    return await performRequest(context, {
      method: 'get',
      path: `/business/invite/` + invite,
      params: {},
    });
  },

  createDraft: async (context, orderId, params) => {
    return await performRequest(context, {
      method: 'post',
      path: `/orders/${orderId}/draft`,
      params,
    });
  },

  deleteDraft: async (context, orderId) => {
    return await performRequest(context, {
      method: 'delete',
      path: `/drafts/${orderId}`,
    });
  },

  getAvailableDates: async (context, params, token) => {
    return await performRequest(context, {
      method: 'post',
      path: `/business/dates`,
      params,
      token,
    });
  },

  reviewTask: async (context, taskId, params) => {
    return await performRequest(context, {
      method: 'put',
      path: `/tasks/` + taskId + '/review',
      params,
    });
  },
};
